import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Tabs, Tab, Chip, Button, Select, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TableSortLabel,
  CircularProgress, Alert, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  PersonOff as PersonOffIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { getLoginActivity, getUserPageViews } from '../services/api';
import PageSearchBar from '../components/PageSearchBar';

// Format seconds into a compact "Hh Mm" / "Mm" string.
function fmtDuration(seconds) {
  if (seconds == null) return '—';
  const s = Number(seconds);
  if (!isFinite(s) || s < 1) return '<1m';
  if (s < 60) return '<1m';
  if (s < 3600) return `${Math.round(s / 60)}m`;
  const h = s / 3600;
  return h < 10 ? `${h.toFixed(1)}h` : `${Math.round(h)}h`;
}

function fmtDateTime(d) {
  if (!d) return '—';
  try {
    return format(new Date(d), 'MMM d, h:mm a');
  } catch {
    return '—';
  }
}

function csvEscape(v) {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
}

// Generic comparator that handles strings, numbers, dates, and nulls.
// Nulls always sort to the bottom regardless of direction — "missing" is
// not a valid value to lead with.
function compareValues(a, b, direction) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  let cmp;
  if (typeof a === 'number' && typeof b === 'number') {
    cmp = a - b;
  } else if (a instanceof Date || b instanceof Date) {
    cmp = new Date(a).getTime() - new Date(b).getTime();
  } else {
    // Try numeric coercion first (active_seconds etc come back as strings).
    const na = Number(a), nb = Number(b);
    if (!isNaN(na) && !isNaN(nb) && a !== '' && b !== '') {
      cmp = na - nb;
    } else {
      cmp = String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
    }
  }
  return direction === 'desc' ? -cmp : cmp;
}

export default function LoginActivity() {
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');

  // Sort state per tab. Tom 2026-04-30: standing rule — all columns must
  // be sortable. Default sorts:
  //   Logged-in tab — active_seconds DESC (matches API default)
  //   Not-logged-in tab — user_role ASC, then user_name ASC
  const [sortLoggedIn, setSortLoggedIn] = useState({ key: 'active_seconds', direction: 'desc' });
  const [sortNotLoggedIn, setSortNotLoggedIn] = useState({ key: 'user_role', direction: 'asc' });

  // Per-user activity drill-down
  const [drillUser, setDrillUser] = useState(null);    // { id, name, email, role }
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState(null);

  const openDrill = async (row) => {
    const userId = row.user_id || row.id;
    if (!userId) return;
    setDrillUser({
      id: userId,
      name: row.user_name || row.name,
      email: row.user_email || row.email,
      role: row.user_role || row.role,
    });
    setDrillData(null);
    setDrillError(null);
    setDrillLoading(true);
    try {
      const res = await getUserPageViews(userId, days);
      setDrillData(res.data);
    } catch (err) {
      setDrillError(err.response?.data?.error || 'Could not load activity');
    } finally {
      setDrillLoading(false);
    }
  };

  const closeDrill = () => {
    setDrillUser(null);
    setDrillData(null);
    setDrillError(null);
  };

  const toggleSort = (current, setter, key) => {
    if (current.key === key) {
      setter({ key, direction: current.direction === 'asc' ? 'desc' : 'asc' });
    } else {
      // First click: numeric/date columns default to desc (most relevant
      // first), text columns to asc (alphabetical).
      const numericKeys = new Set(['sessions', 'last_login', 'last_active', 'last_active_ever', 'active_seconds', 'avg_seconds', 'longest_seconds']);
      setter({ key, direction: numericKeys.has(key) ? 'desc' : 'asc' });
    }
  };

  const load = async (lookbackDays) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getLoginActivity(lookbackDays);
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load login activity.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]);

  const loggedIn = useMemo(() => {
    let rows = data?.logged_in_users || [];
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(r => (r.user_name || '').toLowerCase().includes(q)
        || (r.user_role || '').toLowerCase().includes(q)
        || (r.user_email || '').toLowerCase().includes(q));
    }
    const { key, direction } = sortLoggedIn;
    return [...rows].sort((a, b) => compareValues(a[key], b[key], direction));
  }, [data, search, sortLoggedIn]);

  const notLoggedIn = useMemo(() => {
    let rows = data?.not_logged_in_users || [];
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(r => (r.user_name || '').toLowerCase().includes(q)
        || (r.user_role || '').toLowerCase().includes(q)
        || (r.user_team || '').toLowerCase().includes(q));
    }
    const { key, direction } = sortNotLoggedIn;
    return [...rows].sort((a, b) => {
      const primary = compareValues(a[key], b[key], direction);
      if (primary !== 0) return primary;
      // Stable secondary: name asc when role/team are equal.
      return compareValues(a.user_name, b.user_name, 'asc');
    });
  }, [data, search, sortNotLoggedIn]);

  const handleExport = () => {
    if (!data) return;
    const lines = [];
    lines.push(['user_name','role','team','email','sessions','last_active','last_login','active_hours_capped','raw_hours_uncapped','longest_session_hours'].join(','));
    for (const r of data.logged_in_users || []) {
      lines.push([
        csvEscape(r.user_name),
        csvEscape(r.user_role),
        csvEscape(r.user_team),
        csvEscape(r.user_email),
        r.sessions,
        r.last_active || '',
        r.last_login || '',
        ((r.active_seconds || 0) / 3600).toFixed(2),
        ((r.raw_seconds    || 0) / 3600).toFixed(2),
        ((r.longest_seconds|| 0) / 3600).toFixed(2),
      ].join(','));
    }
    lines.push('');
    lines.push(['INACTIVE'].join(','));
    lines.push(['user_name','role','team','email','last_active_ever'].join(','));
    for (const r of data.not_logged_in_users || []) {
      lines.push([
        csvEscape(r.user_name),
        csvEscape(r.user_role),
        csvEscape(r.user_team),
        csvEscape(r.user_email),
        r.last_active_ever || 'never',
      ].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const today = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `command-center-logins-${days}d-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1280, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Command Center — Login Activity</Typography>
          <Typography variant="body2" color="text.secondary">
            Owner-only view. Per-user sessions, active hours, and the list of accounts that haven't logged in.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">Lookback</Typography>
          <Select size="small" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <MenuItem value={7}>7 days</MenuItem>
            <MenuItem value={14}>14 days</MenuItem>
            <MenuItem value={30}>30 days</MenuItem>
            <MenuItem value={60}>60 days</MenuItem>
            <MenuItem value={90}>90 days</MenuItem>
          </Select>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            disabled={!data || loading}
            onClick={handleExport}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        <Typography variant="caption">
          <strong>Active time</strong> is each session capped at 4h to avoid counting tabs left open in the background.
          The CSV export also includes the raw uncapped hours so you can sanity-check anything.
        </Typography>
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {data && (
        <Paper variant="outlined">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab
                icon={<PersonIcon fontSize="small" />}
                iconPosition="start"
                label={`Active (${data.summary?.total_logged_in ?? 0})`}
              />
              <Tab
                icon={<PersonOffIcon fontSize="small" />}
                iconPosition="start"
                label={`Inactive (${data.summary?.total_not_logged_in ?? 0})`}
              />
            </Tabs>
            <PageSearchBar
              placeholder="Search…"
              value={search}
              onChange={(v) => setSearch(v)}
            />
          </Box>

          {tab === 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    {[
                      { key: 'user_name',       label: 'User' },
                      { key: 'user_role',       label: 'Role' },
                      { key: 'sessions',        label: 'Sessions',             align: 'right' },
                      { key: 'last_active',     label: 'Last active (ET)' },
                      { key: 'last_login',      label: 'Last login (ET)' },
                      { key: 'active_seconds',  label: 'Active time (capped)', align: 'right' },
                      { key: 'avg_seconds',     label: 'Avg / session',        align: 'right' },
                      { key: 'longest_seconds', label: 'Longest',              align: 'right' },
                      { key: '__activity',      label: '',                     align: 'center', sortable: false },
                    ].map((col) => (
                      <TableCell
                        key={col.key}
                        align={col.align || 'left'}
                        sortDirection={sortLoggedIn.key === col.key ? sortLoggedIn.direction : false}
                        sx={{ fontWeight: 700 }}
                      >
                        {col.sortable === false ? col.label : (
                          <TableSortLabel
                            active={sortLoggedIn.key === col.key}
                            direction={sortLoggedIn.key === col.key ? sortLoggedIn.direction : 'asc'}
                            onClick={() => toggleSort(sortLoggedIn, setSortLoggedIn, col.key)}
                          >
                            {col.label}
                          </TableSortLabel>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loggedIn.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No matches.
                      </TableCell>
                    </TableRow>
                  )}
                  {loggedIn.map((r) => (
                    <TableRow key={r.user_id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">{r.user_name}</Typography>
                          {r.user_email && (
                            <Typography variant="caption" color="text.secondary">{r.user_email}</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {r.user_role && <Chip label={r.user_role} size="small" variant="outlined" />}
                      </TableCell>
                      <TableCell align="right">{r.sessions}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{fmtDateTime(r.last_active)}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{fmtDateTime(r.last_login)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtDuration(r.active_seconds)}</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary' }}>{fmtDuration(r.avg_seconds)}</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary' }}>{fmtDuration(r.longest_seconds)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="What pages did they visit?">
                          <IconButton size="small" onClick={() => openDrill(r)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 1 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    {[
                      { key: 'user_name',        label: 'User' },
                      { key: 'user_role',        label: 'Role' },
                      { key: 'user_team',        label: 'Team' },
                      { key: 'user_email',       label: 'Email' },
                      { key: 'last_active_ever', label: 'Last active ever' },
                    ].map((col) => (
                      <TableCell
                        key={col.key}
                        sortDirection={sortNotLoggedIn.key === col.key ? sortNotLoggedIn.direction : false}
                        sx={{ fontWeight: 700 }}
                      >
                        <TableSortLabel
                          active={sortNotLoggedIn.key === col.key}
                          direction={sortNotLoggedIn.key === col.key ? sortNotLoggedIn.direction : 'asc'}
                          onClick={() => toggleSort(sortNotLoggedIn, setSortNotLoggedIn, col.key)}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notLoggedIn.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        Everyone has been active within the lookback window. Nice.
                      </TableCell>
                    </TableRow>
                  )}
                  {notLoggedIn.map((r) => (
                    <TableRow key={r.user_id} hover>
                      <TableCell><Typography variant="body2" fontWeight="medium">{r.user_name}</Typography></TableCell>
                      <TableCell>
                        {r.user_role && <Chip label={r.user_role} size="small" variant="outlined" />}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{r.user_team || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{r.user_email}</Typography>
                      </TableCell>
                      <TableCell>
                        {r.last_active_ever
                          ? <Typography variant="caption" color="text.secondary">{fmtDateTime(r.last_active_ever)}</Typography>
                          : <Chip label="never" size="small" color="error" variant="outlined" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Per-user activity drill-down dialog */}
      <Dialog
        open={!!drillUser}
        onClose={closeDrill}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6">
              {drillUser?.name || 'User activity'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {drillUser?.role}{drillUser?.email ? ` · ${drillUser.email}` : ''} · last {days} days
            </Typography>
          </Box>
          <IconButton onClick={closeDrill} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {drillLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {drillError && <Alert severity="error" sx={{ mb: 2 }}>{drillError}</Alert>}
          {drillData && (
            <>
              <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total page views</Typography>
                  <Typography variant="h5">{drillData.total_views}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Distinct pages</Typography>
                  <Typography variant="h5">{drillData.distinct_routes}</Typography>
                </Box>
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>Pages by visit count</Typography>
              {drillData.routes.length === 0 ? (
                <Alert severity="info">No tracked page views in this window. (Tracking started 2026-05-09.)</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Route</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Views</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">% of total</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>First viewed (ET)</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Last viewed (ET)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {drillData.routes.map((r) => (
                        <TableRow key={r.route} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{r.route}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{r.views}</TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary' }}>
                            {drillData.total_views > 0 ? `${Math.round((r.views / drillData.total_views) * 100)}%` : '—'}
                          </TableCell>
                          <TableCell><Typography variant="caption" color="text.secondary">{fmtDateTime(r.first_viewed)}</Typography></TableCell>
                          <TableCell><Typography variant="caption">{fmtDateTime(r.last_viewed)}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {drillData.recent && drillData.recent.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Recent navigations (latest {drillData.recent.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>When (ET)</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Route</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Path</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {drillData.recent.map((r, i) => (
                          <TableRow key={i} hover>
                            <TableCell><Typography variant="caption">{fmtDateTime(r.viewed_at)}</Typography></TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.route}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>
                              {r.path && r.path !== r.route ? r.path : ''}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDrill}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
